(() => {
  const ROOT_SELECTOR = '[data-collection-context-root]';

  if (!window.__brilintonCollectionContextHistoryPatched) {
    window.__brilintonCollectionContextHistoryPatched = true;
    ['pushState', 'replaceState'].forEach((method) => {
      const original = history[method];
      history[method] = function (...args) {
        const result = original.apply(this, args);
        window.dispatchEvent(new Event('brilinton:urlchange'));
        return result;
      };
    });
  }
  const norm = (value) => String(value || '').trim().toLowerCase().replace(/[\s_]+/g, '-');
  const sizeNorm = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '');
  const ROYAL_DISCOVERY_DELAY = 1400;
  const ROYAL_DEEPLINK_DISCOVERY_DELAY = 1800;
  const ROYAL_DISCOVERY_RETRY_DELAY = 350;


  const visibleHeaderBottom = () => {
    const selectors = [
      '.announcement-bar-section',
      '.announcement-bar',
      '.section-header',
      'sticky-header',
      '.header-wrapper',
      'header.header'
    ];
    let bottom = 0;
    document.querySelectorAll(selectors.join(',')).forEach((element) => {
      if (!element || !element.getBoundingClientRect) return;
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return;
      const rect = element.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1 || rect.bottom <= 0 || rect.top > 180) return;
      bottom = Math.max(bottom, rect.bottom);
    });
    // Mask edges must never round down-screen and expose a fractional seam.
    return Math.max(0, Math.floor(bottom));
  };

  class CollectionContextNavigation {
    constructor(root) {
      if (!root || root.dataset.collectionContextReady === 'true') return;
      root.dataset.collectionContextReady = 'true';
      this.root = root;
      this.kind = root.dataset.contextKind || '';
      this.mode = root.dataset.selectorMode || 'popup';
      this.routeMode = root.dataset.appleRouteMode || 'query';
      this.iphoneRouteMode = root.dataset.iphoneRouteMode || 'query';
      this.deviceMemory = window.BrilintonDeviceContext || null;
      this.openButton = root.querySelector('[data-collection-context-open]');
      this.floatingButton = root.querySelector('[data-collection-context-floating-open]');
      this.floatingLabel = this.floatingButton?.querySelector('[data-collection-selector-label]') || null;
      this.studioButton = root.querySelector('[data-collection-studio-open]');
      this.value = root.querySelector('[data-collection-context-value]');
      this.thumb = root.querySelector('[data-collection-context-thumb]');
      this.count = root.querySelector('[data-collection-context-count]');
      this.originalCount = this.count ? Number.parseInt(this.count.dataset.collectionCount || '0', 10) : 0;
      this.drawer = root.querySelector('[data-collection-context-drawer]');
      this.inlineSelector = root.querySelector('[data-collection-context-inline]');
      this.sheetDismissLayer = root.querySelector('[data-collection-context-sheet-dismiss]');
      this.sheetDragHandle = this.inlineSelector?.querySelector('[data-collection-context-drag-handle]') || null;
      this.selectorSurface = this.mode === 'hybrid' ? this.inlineSelector : this.drawer;
      this.surfaceName = 'collection-context-selector';
      this.surfaceTrigger = this.openButton;
      this.options = this.selectorSurface
        ? Array.from(this.selectorSurface.querySelectorAll('[data-collection-context-choice]'))
        : [];
      this.optionRails = this.selectorSurface
        ? Array.from(this.selectorSurface.querySelectorAll('.collection-context__options'))
        : [];
      this.selectedSize = root.dataset.currentAppleSize || '';
      this.selectedModel = root.dataset.defaultRoyalSlug || '';
      this.selectedIphoneDevice = '';
      this.currentIphoneGeneration = root.dataset.currentIphoneGeneration || '';
      this.boundPopState = () => this.syncFromUrl();
      this.boundRoyalChange = (event) => this.onExternalRoyalChange(event);
      this.boundProductsAdded = () => this.onProductsAdded();
      this.boundKeyDown = (event) => {
        if (event.key !== 'Escape' || !this.isOpen()) return;
        this.userInteracted = true;
        this.clearAutoOpen();
        if (this.mode === 'hybrid') this.markHybridDismissed();
        this.setOpen(false);
      };
      this.boundHeaderMenuActivity = (event) => this.onHeaderMenuActivity(event);
      this.autoOpenTimer = null;
      this.userInteracted = false;
      this.stickyStart = 0;
      this.stickyFrame = 0;
      this.boundStickyCheck = () => this.queueStickyCheck();
      this.boundStickyMeasure = () => {
        this.measureStickyStart();
        this.syncOptionRailAlignment();
      };
      this.boundOverlayMeasure = () => {
        if (this.isOpen()) this.syncOverlayTop();
      };
      this.boundLayoutRefresh = (event) => this.onLayoutRefresh(event);
      this.hybridDragPointerId = null;
      this.hybridDragStartY = 0;
      this.hybridDragDistance = 0;

      if (this.mode === 'popup' && this.drawer && this.drawer.parentElement !== document.body) {
        document.body.appendChild(this.drawer);
      }

      this.bind();
      this.syncFromUrl();
      this.measureStickyStart();
      this.syncOptionRailAlignment();
      this.maybeOpenAppleWatchConfirmationIntent();
      this.maybeOpenHybridByDefault();
      this.maybeAutoOpen();
    }

    bind() {
      const bindSelectorOpen = (button) => {
        if (!button || !this.selectorSurface) return;
        button.addEventListener('click', () => {
          this.surfaceTrigger = button;
          const wasOpen = this.isOpen();
          this.userInteracted = true;
          this.clearAutoOpen();
          if (wasOpen && this.mode === 'hybrid') this.markHybridDismissed();
          this.setOpen(!wasOpen);
        });
      };
      bindSelectorOpen(this.openButton);
      bindSelectorOpen(this.floatingButton);
      if (this.studioButton) {
        this.studioButton.addEventListener('click', () => {
          document.dispatchEvent(new CustomEvent('brilinton:collection-studio-open', {
            detail: { platform: this.studioButton.dataset.studioPlatform || '' }
          }));
        });
      }
      if (this.drawer) {
        this.drawer.addEventListener('click', (event) => {
          const closeTarget = event.target && event.target.closest
            ? event.target.closest('[data-collection-context-close]')
            : null;
          if (!closeTarget || !this.drawer.contains(closeTarget)) return;
          event.preventDefault();
          this.userInteracted = true;
          this.clearAutoOpen();
          this.setOpen(false);
        });
      }
      if (this.inlineSelector) {
        this.inlineSelector.querySelectorAll('[data-collection-context-close]').forEach((closeButton) => {
          closeButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.userInteracted = true;
            this.clearAutoOpen();
            if (this.mode === 'hybrid') this.markHybridDismissed();
            this.setOpen(false);
          });
        });
      }
      if (this.sheetDismissLayer) {
        this.sheetDismissLayer.addEventListener('click', (event) => {
          event.preventDefault();
          this.userInteracted = true;
          this.clearAutoOpen();
          if (this.mode === 'hybrid') this.markHybridDismissed();
          this.setOpen(false);
        });
      }
      this.bindHybridSheetDrag();
      this.options.forEach((option) => {
        option.addEventListener('click', (event) => this.selectOption(option, event));
      });
      window.addEventListener('popstate', this.boundPopState);
      window.addEventListener('brilinton:urlchange', this.boundPopState);
      document.addEventListener('royal-pop:model-change', this.boundRoyalChange);
      document.addEventListener('brilinton:collection-products-added', this.boundProductsAdded);
      document.addEventListener('keydown', this.boundKeyDown);
      document.addEventListener('click', this.boundHeaderMenuActivity);
      document.addEventListener('toggle', this.boundHeaderMenuActivity, true);
      window.addEventListener('scroll', this.boundStickyCheck, { passive: true });
      window.addEventListener('resize', this.boundStickyMeasure, { passive: true });
      window.addEventListener('brilinton:layout-refresh', this.boundLayoutRefresh, { passive: true });
      window.addEventListener('resize', this.boundOverlayMeasure, { passive: true });
      window.addEventListener('load', this.boundStickyMeasure, { once: true });
    }

    usesHybridSheet() {
      return this.mode === 'hybrid';
    }

    syncOptionRailAlignment() {
      this.optionRails.forEach((rail) => {
        const style = window.getComputedStyle(rail);
        const visibleItems = Array.from(rail.children).filter((item) => {
          if (!item || item.hidden) return false;
          const itemStyle = window.getComputedStyle(item);
          return itemStyle.display !== 'none' && itemStyle.visibility !== 'hidden';
        });
        const gap = Number.parseFloat(style.columnGap || style.gap || '0') || 0;
        const padding = (Number.parseFloat(style.paddingLeft || '0') || 0)
          + (Number.parseFloat(style.paddingRight || '0') || 0);
        const contentWidth = visibleItems.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0)
          + Math.max(0, visibleItems.length - 1) * gap
          + padding;
        const overflows = contentWidth > rail.clientWidth + 1;
        rail.classList.toggle('is-overflowing', overflows);
        if (!overflows && rail.scrollLeft) rail.scrollLeft = 0;
      });
    }

    headerMenuDetails() {
      return document.querySelector('#Details-menu-drawer-container, header-drawer details');
    }

    isHeaderMenuOpen() {
      const details = this.headerMenuDetails();
      if (!details) return false;
      const summary = details.querySelector(':scope > summary.header__icon--menu, :scope > summary');
      return details.hasAttribute('open')
        || details.classList.contains('menu-opening')
        || summary?.getAttribute('aria-expanded') === 'true';
    }

    onHeaderMenuActivity(event) {
      const clickSummary = event.type === 'click' && event.target?.closest
        ? event.target.closest('#Details-menu-drawer-container > summary.header__icon--menu, header-drawer summary.header__icon--menu, header .header__icon--menu')
        : null;
      const toggledDetails = event.type === 'toggle' && event.target?.matches
        && event.target.matches('#Details-menu-drawer-container, header-drawer details')
        ? event.target
        : null;

      if (!clickSummary && !toggledDetails) return;

      const details = toggledDetails || clickSummary.closest('details');
      const summary = clickSummary || details?.querySelector(':scope > summary');
      const menuIsOpening = details && (
        details.hasAttribute('open')
        || details.classList.contains('menu-opening')
        || summary?.getAttribute('aria-expanded') === 'true'
      );

      if (menuIsOpening && this.isOpen()) this.setOpen(false);
    }

    isOpen() {
      return this.root.classList.contains('is-open') || this.root.classList.contains('is-selector-sheet-open');
    }

    resetHybridSheetDrag() {
      const pointerId = this.hybridDragPointerId;
      this.hybridDragPointerId = null;
      this.hybridDragStartY = 0;
      this.hybridDragDistance = 0;
      if (pointerId != null && this.sheetDragHandle) {
        try {
          if (this.sheetDragHandle.hasPointerCapture?.(pointerId)) {
            this.sheetDragHandle.releasePointerCapture?.(pointerId);
          }
        } catch (_error) {
          // A suspended WebView may already have discarded pointer capture.
        }
      }
      if (!this.inlineSelector) return;
      this.inlineSelector.classList.remove('is-dragging');
      this.inlineSelector.style.removeProperty('--collection-context-sheet-drag-y');
    }

    bindHybridSheetDrag() {
      if (!this.sheetDragHandle || !this.inlineSelector || this.mode !== 'hybrid') return;

      const finish = (event) => {
        if (this.hybridDragPointerId == null) return;
        if (event?.pointerId != null && event.pointerId !== this.hybridDragPointerId) return;
        const shouldClose = this.hybridDragDistance >= 64;
        this.resetHybridSheetDrag();
        if (shouldClose) {
          this.userInteracted = true;
          this.markHybridDismissed();
          this.setOpen(false);
        }
      };

      this.sheetDragHandle.addEventListener('pointerdown', (event) => {
        if (!this.usesHybridSheet() || !this.isOpen()) return;
        if (event.target.closest('[data-collection-context-close]')) return;
        this.hybridDragPointerId = event.pointerId;
        this.hybridDragStartY = event.clientY;
        this.hybridDragDistance = 0;
        this.inlineSelector.classList.add('is-dragging');
        this.sheetDragHandle.setPointerCapture?.(event.pointerId);
      });

      this.sheetDragHandle.addEventListener('pointermove', (event) => {
        if (this.hybridDragPointerId !== event.pointerId) return;
        this.hybridDragDistance = Math.max(0, event.clientY - this.hybridDragStartY);
        this.inlineSelector.style.setProperty('--collection-context-sheet-drag-y', `${this.hybridDragDistance}px`);
      });

      this.sheetDragHandle.addEventListener('pointerup', finish);
      this.sheetDragHandle.addEventListener('pointercancel', finish);
      this.sheetDragHandle.addEventListener('lostpointercapture', finish);
    }

    onLayoutRefresh(event) {
      const reason = event?.detail?.reason || '';
      if (
        reason !== 'visibility-resume'
        && reason !== 'bfcache-restore'
        && reason !== 'page-lifecycle-resume'
      ) return;

      this.clearAutoOpen();
      this.resetHybridSheetDrag();

      // Mobile browsers/WebViews can restore fixed layers and pointer capture out of
      // sync after suspension. Always return the selector to its canonical closed
      // state on resume, but do not record that recovery as a user dismissal.
      if (this.mode === 'hybrid') this.setOpen(false);

      this.syncFromUrl();
      this.measureStickyStart();
      this.syncOptionRailAlignment();
      if (this.isOpen()) this.syncOverlayTop();

      // Royal Pop discovery may be reintroduced only when the existing session
      // policy still allows it. Explicit selection/dismissal remains authoritative.
      if (this.mode === 'hybrid' && this.kind === 'royal-watchware') {
        this.maybeOpenHybridByDefault();
      }
    }

    syncOverlayTop() {
      const headerBottom = visibleHeaderBottom();
      let overlayTop = headerBottom;
      const rootRect = this.root.getBoundingClientRect();
      const stickyTop = Number.parseFloat(window.getComputedStyle(this.root).top || '0') || 0;
      const isActuallySticky = this.root.classList.contains('is-sticky')
        || (rootRect.top <= stickyTop + 1 && rootRect.bottom > stickyTop + 1);

      // R44-302 — the mask has one stable edge while the selector is open.
      // Natural position starts at the literal masthead bottom so the intentional
      // 1rem banner→navigation gap and compact bar are dimmed. Sticky position
      // starts below the compact bar so the active selector trigger remains clear.
      // Floor rather than ceil: a sub-pixel overlap is harmless; an uncovered
      // fractional pixel creates the visible white seam reported in preview.
      if (isActuallySticky) {
        overlayTop = Math.max(headerBottom, Math.floor(rootRect.bottom));
      } else {
        const masthead = document.querySelector('[data-collection-masthead]');
        const mastheadBottom = masthead?.getBoundingClientRect?.().bottom;
        if (Number.isFinite(mastheadBottom)) overlayTop = Math.max(headerBottom, Math.floor(mastheadBottom));
      }

      const value = `${overlayTop}px`;
      this.root.style.setProperty('--collection-context-overlay-top', value);
      if (this.drawer) this.drawer.style.setProperty('--collection-context-overlay-top', value);
      if (this.sheetDismissLayer) this.sheetDismissLayer.style.setProperty('--collection-context-overlay-top', value);
    }

    measureStickyStart() {
      this.queueStickyCheck();
    }

    queueStickyCheck() {
      if (this.stickyFrame) return;
      this.stickyFrame = window.requestAnimationFrame(() => {
        this.stickyFrame = 0;
        const stickyTop = Number.parseFloat(window.getComputedStyle(this.root).top || '0') || 0;
        const rect = this.root.getBoundingClientRect();
        const isSticky = rect.top <= stickyTop + 1 && rect.bottom > stickyTop + 1;
        this.root.classList.toggle('is-sticky', isSticky);
        // Do not rewrite the fixed mask edge on scroll while a selector is open.
        // The Surface Manager locks the page; resize/layout refresh owns remeasurement.
      });
    }

    hybridStateStorageKey(state) {
      return `brilinton-context-hybrid:${state}:v1:${this.root.dataset.collectionHandle || this.kind}`;
    }

    readHybridState(state) {
      try {
        return window.sessionStorage.getItem(this.hybridStateStorageKey(state)) === '1';
      } catch (_error) {
        return this.root.dataset[`hybrid${state.charAt(0).toUpperCase()}${state.slice(1)}`] === 'true';
      }
    }

    writeHybridState(state) {
      try {
        window.sessionStorage.setItem(this.hybridStateStorageKey(state), '1');
      } catch (_error) {
        this.root.dataset[`hybrid${state.charAt(0).toUpperCase()}${state.slice(1)}`] = 'true';
      }
    }

    markHybridDiscovered() {
      this.writeHybridState('discovered');
    }

    markHybridDismissed() {
      this.markHybridDiscovered();
      this.writeHybridState('dismissed');
    }

    markHybridSelected() {
      this.markHybridDiscovered();
      this.writeHybridState('selected');
    }

    royalOptionForValue(value) {
      const raw = String(value || '').trim();
      if (!raw) return null;
      if (/^\d+$/.test(raw)) return this.options[Number(raw)] || null;
      return this.options.find((option) => norm(option.dataset.contextModel) === norm(raw)) || null;
    }

    royalModelFromUrl() {
      const params = new URLSearchParams(window.location.search || '');
      return this.royalOptionForValue(params.get('model'));
    }

    hasBlockingDiscoverySurface() {
      const surfaceTop = window.BrilintonSurfaceManager?.top?.();
      return Boolean(surfaceTop) || this.isHeaderMenuOpen();
    }

    hasExplicitContext() {
      const params = new URLSearchParams(window.location.search || '');
      if (this.kind === 'apple-watchware') {
        // Browser memory is preference, not explicit collection intent. On the
        // generic Apple Watch Cases route it must never masquerade as an active
        // size filter; the compact selector can ask the shopper to confirm it.
        return Boolean(params.get('size') || this.root.dataset.currentAppleSize || this.selectedSize);
      }
      if (this.kind === 'royal-watchware') return Boolean(this.royalModelFromUrl() || this.deviceMemory?.get('royalPop'));
      return true;
    }

    maybeOpenAppleWatchConfirmationIntent() {
      if (this.kind !== 'apple-watchware' || this.mode !== 'hybrid' || !this.inlineSelector) return;
      if (window.__brilintonPrimarySurfacePriority) {
        document.addEventListener('brilinton:primary-priority-clear', () => this.maybeOpenAppleWatchConfirmationIntent(), { once: true });
        return;
      }
      const pageSize = this.root.dataset.currentAppleSize || '';
      if (!pageSize) return;

      const key = this.deviceMemory?.appleWatchConfirmationSessionKey || 'brilinton.apple-watch.confirm-selector.v1';
      let intent = null;
      try {
        const raw = window.sessionStorage.getItem(key);
        if (raw) intent = JSON.parse(raw);
        window.sessionStorage.removeItem(key);
      } catch (_) {}
      if (!intent?.size || sizeNorm(intent.size) !== sizeNorm(pageSize)) return;
      if (intent.at && Date.now() - Number(intent.at) > 30000) return;

      // This was an intentional internal "Apple Watch Cases" navigation that was
      // personalized to the remembered canonical size page. Open the selector once
      // so the shopper can immediately keep or change that remembered size.
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
        if (this.hasBlockingDiscoverySurface()) return;
        this.setOpen(true);
      }));
    }

    maybeOpenHybridByDefault() {
      if (this.mode !== 'hybrid' || !this.inlineSelector) return;
      if (window.__brilintonPrimarySurfacePriority) {
        document.addEventListener('brilinton:primary-priority-clear', () => this.maybeOpenHybridByDefault(), { once: true });
        return;
      }
      const handle = this.root.dataset.collectionHandle || '';
      const isGenericWatchware = handle === 'apple-watch-cases' || handle === 'royal-pop-cases';
      if (!isGenericWatchware) return;

      // Apple Watch remains a compatibility-first selector: if a size is already explicit,
      // there is no need to interrupt the visitor again.
      if (handle === 'apple-watch-cases') {
        if (this.hasExplicitContext()) return;
        // Browser memory is private preference, not broad-page state. If a
        // remembered size exists on a direct/external visit to Apple Watch Cases,
        // keep the page broad and quiet; the selector may pre-highlight memory when
        // the shopper opens it. Internal generic-category clicks are handled by the
        // shared device-context service and arrive on the canonical size page with
        // a one-shot confirmation selector.
        const rememberedApple = this.deviceMemory?.get('appleWatch');
        if (rememberedApple) return;
        if (this.readHybridState('dismissed') || this.readHybridState('selected')) return;
        this.setOpen(true);
        return;
      }

      // Royal Pop is a product-discovery interaction. A valid model in the URL tells us
      // what to display, not whether the visitor has discovered the selector itself.
      // Passive auto-opening is not a suppression signal; only an explicit model choice
      // or an explicit dismissal suppresses further discovery for the browser session.
      if (this.readHybridState('selected') || this.readHybridState('dismissed')) return;

      const hasValidDeepLinkModel = Boolean(this.royalModelFromUrl());
      const delay = hasValidDeepLinkModel ? ROYAL_DEEPLINK_DISCOVERY_DELAY : ROYAL_DISCOVERY_DELAY;

      const openDiscovery = () => {
        this.autoOpenTimer = null;
        if (this.userInteracted) return;
        if (this.readHybridState('selected') || this.readHybridState('dismissed')) return;

        // Do not compete with Cart, Menu, Account, FAQ, Studio or another managed surface.
        // If the discovery moment lands while something else is open, wait and retry shortly
        // after the competing surface has yielded instead of silently losing the introduction.
        if (this.hasBlockingDiscoverySurface()) {
          this.autoOpenTimer = window.setTimeout(openDiscovery, ROYAL_DISCOVERY_RETRY_DELAY);
          return;
        }

        window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
          if (this.userInteracted || this.readHybridState('selected') || this.readHybridState('dismissed')) return;
          if (this.hasBlockingDiscoverySurface()) {
            this.autoOpenTimer = window.setTimeout(openDiscovery, ROYAL_DISCOVERY_RETRY_DELAY);
            return;
          }
          this.setOpen(true);
          if (!this.isOpen()) this.autoOpenTimer = window.setTimeout(openDiscovery, ROYAL_DISCOVERY_RETRY_DELAY);
        }));
      };

      this.autoOpenTimer = window.setTimeout(openDiscovery, delay);
    }

    autoOpenStorageKey() {
      return `brilinton-context-opened:v3:${this.root.dataset.collectionHandle || this.kind}`;
    }

    hasAutoOpened() {
      try {
        return window.sessionStorage.getItem(this.autoOpenStorageKey()) === '1';
      } catch (_error) {
        return this.root.dataset.autoOpened === 'true';
      }
    }

    markAutoOpened() {
      try {
        window.sessionStorage.setItem(this.autoOpenStorageKey(), '1');
      } catch (_error) {
        this.root.dataset.autoOpened = 'true';
      }
    }

    setOpen(state) {
      if (!this.selectorSurface || !this.openButton) return;
      const nextState = Boolean(state);
      const isPopup = this.mode === 'popup';
      const surfaceManager = window.BrilintonSurfaceManager;

      // The collection selector and the mobile header menu are mutually exclusive
      // surfaces. Never allow the fixed selector sheet to sit above the menu drawer.
      if (nextState && this.isHeaderMenuOpen()) return;

      // Measure before viewport locking so the mask is anchored to the exact visual
      // geometry the shopper clicked. It remains frozen until a real resize/layout
      // refresh; scroll is not a mask-position input while the modal is open.
      if (nextState) this.syncOverlayTop();

      const hybridSheet = this.usesHybridSheet();
      if (nextState && hybridSheet) this.markHybridDiscovered();
      this.root.classList.toggle('is-selector-sheet-open', nextState && hybridSheet);
      this.root.classList.toggle('is-open', nextState && !hybridSheet);
      if (!nextState) this.resetHybridSheetDrag();
      this.openButton.setAttribute('aria-expanded', nextState ? 'true' : 'false');
      if (this.floatingButton) this.floatingButton.setAttribute('aria-expanded', nextState ? 'true' : 'false');

      if (isPopup && this.drawer) {
        this.drawer.classList.toggle('is-open', nextState);
        this.drawer.setAttribute('aria-hidden', nextState ? 'false' : 'true');
      } else if (this.inlineSelector) {
        this.inlineSelector.setAttribute('aria-hidden', nextState ? 'false' : 'true');
      }

      // This class is now a selector-UI state for both popup and hybrid paths.
      // Actual viewport ownership belongs to BrilintonSurfaceManager when present.
      document.body.classList.toggle('collection-context-open', nextState);

      if (nextState && surfaceManager) {
        surfaceManager.register(this.surfaceName, {
          root: () => this.selectorSurface,
          close: () => this.setOpen(false),
        });
        // R44-303 — selectors must not fixed-position the storefront body. Apply
        // visible selector state first, then acquire the normal overflow lock so a
        // managed lock can never precede the UI it belongs to.
        surfaceManager.open(this.surfaceName, {
          trigger: this.surfaceTrigger || this.openButton,
          lockMode: 'overflow',
        });
      }

      if (!nextState && surfaceManager?.isOpen(this.surfaceName)) {
        surfaceManager.close(this.surfaceName, { restoreFocus: true });
      }

      if (nextState) {
        if (isPopup) this.markAutoOpened();
        window.requestAnimationFrame(() => {
          this.syncOptionRailAlignment();
          this.centerSelected();
        });
      } else {
        this.surfaceTrigger = this.openButton;
        this.queueStickyCheck();
      }
    }

    clearAutoOpen() {
      if (!this.autoOpenTimer) return;
      window.clearTimeout(this.autoOpenTimer);
      this.autoOpenTimer = null;
    }

    maybeAutoOpen() {
      if (this.mode !== 'popup' || !this.drawer || this.root.dataset.autoOpen !== 'true') return;
      if (window.__brilintonPrimarySurfacePriority) {
        document.addEventListener('brilinton:primary-priority-clear', () => this.maybeAutoOpen(), { once: true });
        return;
      }
      const mode = this.root.dataset.autoOpenMode || 'once';
      const delay = Math.max(0, Number.parseInt(this.root.dataset.autoOpenDelay || '0', 10) || 0);

      if (mode !== 'always') {
        const params = new URLSearchParams(window.location.search || '');
        const alreadyHasContext = this.kind.includes('apple')
          ? Boolean(params.get('size') || this.root.dataset.currentAppleSize || this.selectedSize)
          : Boolean(params.get('model') || this.selectedModel || this.deviceMemory?.get('royalPop'));
        if (alreadyHasContext) return;

        if (this.hasAutoOpened()) return;
      }

      const open = () => {
        this.autoOpenTimer = null;
        if (this.userInteracted) return;
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.setOpen(true)));
      };
      if (delay > 0) this.autoOpenTimer = window.setTimeout(open, delay);
      else open();
    }

    selectOption(option, event) {
      const size = option.dataset.contextSize || '';
      const model = option.dataset.contextModel || '';
      const iphoneDevice = option.dataset.contextDevice || '';

      if (!size && !model && !iphoneDevice) return;
      this.userInteracted = true;
      this.clearAutoOpen();

      if (iphoneDevice) {
        const generation = option.dataset.contextGeneration || '';
        const sameGeneration = generation && this.currentIphoneGeneration
          && norm(generation) === norm(this.currentIphoneGeneration);

        if (option.tagName === 'A' && this.iphoneRouteMode === 'collection' && !sameGeneration) {
          this.deviceMemory?.rememberIphone(iphoneDevice, 'collection-context-navigation');
          this.decorateIphoneRouteLink(option, iphoneDevice);
          this.setOpen(false);
          return;
        }

        event.preventDefault();
        this.applyIphoneDevice(option, true);
        this.setOpen(false);
        return;
      }

      if (size) {
        const genericAppleRoute = this.root.dataset.collectionHandle === 'apple-watch-cases'
          && this.routeMode === 'collection';
        if (this.mode === 'hybrid' && !genericAppleRoute) this.markHybridSelected();
        this.deviceMemory?.rememberAppleWatch({ size, series: option.dataset.contextSeries || '' }, 'collection-context-navigation');
        const activeSize = this.selectedSize || this.root.dataset.currentAppleSize || '';
        const isCurrentSize = sizeNorm(size) === sizeNorm(activeSize);

        if (isCurrentSize) {
          event.preventDefault();
          this.setOpen(false);
          return;
        }

        if (option.tagName === 'A' && this.routeMode === 'collection') {
          this.setOpen(false);
          return;
        }
        event.preventDefault();
        this.applyAppleSize(size, option.dataset.contextSeries || '', option.dataset.contextImage || '', true);
        this.setOpen(false);
        return;
      }

      event.preventDefault();
      if (this.mode === 'hybrid') this.markHybridSelected();
      this.applyRoyalModel(option, true);
      this.setOpen(false);
    }

    applyAppleSize(size, series, image, updateHistory) {
      this.selectedSize = size;
      this.setSelectedOption('size', size);
      this.renderAppleValue(size, series, image);
      this.updateEditionLinks(size);
      this.filterProductsBySize(size);
      this.updateWatchwareProductLinks({ size });

      if (updateHistory) {
        const url = new URL(window.location.href);
        url.searchParams.set('size', size);
        if (this.kind === 'apple-bandware') {
          url.searchParams.set('device', 'apple-watch');
          url.searchParams.set('mode', 'direct');
        }
        history.pushState({ collectionContextSize: size }, '', `${url.pathname}${url.search}${url.hash}`);
        if (this.kind === 'apple-bandware') window.dispatchEvent(new PopStateEvent('popstate'));
      }
    }

    applyRoyalModel(option, updateHistory) {
      const slug = option.dataset.contextModel || '';
      const name = option.dataset.contextName || slug;
      const crown = option.dataset.contextCrown || '';
      const optionImage = option.querySelector('img');
      const image = option.dataset.contextImage || optionImage?.currentSrc || optionImage?.src || '';
      this.selectedModel = slug;
      this.setSelectedOption('model', slug);
      this.renderRoyalValue(name, crown, image);
      this.updateWatchwareProductLinks({ model: slug });

      if (updateHistory) {
        this.deviceMemory?.rememberRoyalPop({ modelSlug: slug, name }, 'collection-context-navigation');
        const url = new URL(window.location.href);
        url.searchParams.set('model', slug);
        url.searchParams.delete('device');
        if (this.kind === 'royal-bandware') {
          url.searchParams.set('device', 'royal-pop');
          url.searchParams.set('mode', 'direct');
        }
        history.pushState({ collectionContextModel: slug }, '', `${url.pathname}${url.search}${url.hash}`);
        document.dispatchEvent(new CustomEvent('royal-pop:model-change', {
          detail: { modelSlug: slug, source: 'collection-context-navigation' }
        }));
        if (this.kind === 'royal-bandware') window.dispatchEvent(new PopStateEvent('popstate'));
      }
    }

    attributionParams() {
      const source = new URLSearchParams(window.location.search || '');
      const result = [];
      source.forEach((value, key) => {
        const normalized = String(key || '').toLowerCase();
        if (normalized.startsWith('utm_') || ['gclid', 'fbclid', 'msclkid', 'ttclid'].includes(normalized)) {
          result.push([key, value]);
        }
      });
      return result;
    }

    decorateIphoneRouteLink(link, deviceSlug) {
      if (!link?.href || !deviceSlug) return;
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('iphone_device', deviceSlug);
      this.attributionParams().forEach(([key, value]) => {
        if (!url.searchParams.has(key)) url.searchParams.set(key, value);
      });
      link.href = `${url.pathname}${url.search}${url.hash}`;
    }

    iphoneOptionForValue(value) {
      const raw = String(value || '').trim();
      if (!raw) return null;
      return this.options.find((option) => norm(option.dataset.contextDevice) === norm(raw)) || null;
    }

    iphoneVariantId(item, deviceSlug) {
      const map = String(item?.dataset?.iphoneVariantMap || '').trim();
      if (!map || !deviceSlug) return '';
      const target = norm(deviceSlug);
      for (const pair of map.split('|')) {
        const separator = pair.indexOf(':');
        if (separator < 1) continue;
        const key = pair.slice(0, separator);
        const value = pair.slice(separator + 1);
        if (norm(key) === target) return value;
      }
      return '';
    }

    applyIphoneDevice(option, updateHistory) {
      const slug = option.dataset.contextDevice || '';
      if (!slug) return;
      const generation = option.dataset.contextGeneration || '';
      const model = option.dataset.contextModel || '';
      const name = option.dataset.contextName || [generation, model].filter(Boolean).join(' ');
      const optionImage = option.querySelector('img');
      const image = option.dataset.contextImage || optionImage?.currentSrc || optionImage?.src || '';

      this.selectedIphoneDevice = slug;
      this.setSelectedOption('iphone', slug);
      this.renderIphoneValue(name, generation, image);
      this.filterProductsByIphone(slug);
      this.updateIphoneProductLinks(slug);

      if (updateHistory) {
        this.deviceMemory?.rememberIphone(slug, 'collection-context-navigation');
        const url = new URL(window.location.href);
        url.searchParams.set('iphone_device', slug);
        history.pushState({ collectionContextIphoneDevice: slug }, '', `${url.pathname}${url.search}${url.hash}`);
        document.dispatchEvent(new CustomEvent('brilinton:iphone-device-change', {
          detail: { device: slug, generation, model, source: 'collection-context-navigation' }
        }));
      }
    }

    renderIphoneValue(name, generation, image) {
      if (this.value) {
        this.value.textContent = '';
        const display = name || generation || 'Select your iPhone';
        this.value.append(document.createTextNode(display));
        if (!name && generation) {
          const meta = document.createElement('small');
          meta.textContent = 'Select model';
          this.value.appendChild(meta);
        }
      }
      if (this.thumb) {
        if (image) {
          let img = this.thumb.querySelector('img');
          if (!img) {
            img = document.createElement('img');
            img.className = 'collection-context__thumb-image';
            img.alt = '';
            this.thumb.appendChild(img);
          }
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = image;
          this.thumb.hidden = false;
          this.openButton?.classList.remove('collection-context__current--no-thumb');
        } else if (!name) {
          this.thumb.hidden = true;
          this.openButton?.classList.add('collection-context__current--no-thumb');
        }
      }
    }

    filterProductsByIphone(deviceSlug) {
      if (!this.kind.includes('iphone')) return;
      document.querySelectorAll('[data-collection-product]').forEach((item) => {
        const map = String(item.dataset.iphoneVariantMap || '').trim();
        const shouldShow = !deviceSlug || !map || Boolean(this.iphoneVariantId(item, deviceSlug));
        item.hidden = !shouldShow;
      });
      this.updateCount();
    }

    updateIphoneProductLinks(deviceSlug) {
      if (!this.kind.includes('iphone') || !deviceSlug) return;
      document.querySelectorAll('[data-collection-product]').forEach((item) => {
        const variantId = this.iphoneVariantId(item, deviceSlug);
        item.querySelectorAll('a[href*="/products/"]').forEach((link) => {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('iphone_device', deviceSlug);
          if (variantId) url.searchParams.set('variant', variantId);
          else url.searchParams.delete('variant');
          this.attributionParams().forEach(([key, value]) => {
            if (!url.searchParams.has(key)) url.searchParams.set(key, value);
          });
          link.href = `${url.pathname}${url.search}${url.hash}`;
        });
      });
    }

    setSelectedOption(type, value) {
      this.options.forEach((option) => {
        const optionValue = type === 'size'
          ? option.dataset.contextSize
          : type === 'iphone'
            ? option.dataset.contextDevice
            : option.dataset.contextModel;
        const selected = norm(optionValue) === norm(value);
        option.classList.toggle('is-selected', selected);
        if (option.tagName === 'BUTTON') option.setAttribute('aria-pressed', selected ? 'true' : 'false');
        if (option.tagName === 'A') {
          if (selected) option.setAttribute('aria-current', 'page');
          else option.removeAttribute('aria-current');
        }
      });
    }

    renderAppleValue(size, series, image) {
      if (this.value) {
        this.value.textContent = '';
        this.value.append(document.createTextNode(size || 'Select size'));
        if (series) {
          const meta = document.createElement('small');
          meta.textContent = series;
          this.value.appendChild(meta);
        }
      }
      if (this.thumb) {
        if (image) {
          let img = this.thumb.querySelector('img');
          if (!img) {
            img = document.createElement('img');
            img.className = 'collection-context__thumb-image';
            img.alt = '';
            this.thumb.appendChild(img);
          }
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = image;
          this.thumb.hidden = false;
        } else if (!size) {
          this.thumb.hidden = true;
        }
      }
    }

    renderRoyalValue(name, crown, image) {
      if (this.value) {
        this.value.textContent = '';
        this.value.append(document.createTextNode(name || 'Select model'));
        if (crown) {
          const meta = document.createElement('small');
          meta.textContent = crown;
          this.value.appendChild(meta);
        }
      }
      if (this.thumb) {
        this.thumb.classList.toggle('collection-context__thumb--12', /12/.test(String(crown || '')));
        if (image) {
          let img = this.thumb.querySelector('img');
          if (!img) {
            img = document.createElement('img');
            img.className = 'collection-context__thumb-image';
            img.alt = '';
            this.thumb.appendChild(img);
          }
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = image;
          this.thumb.hidden = false;
        }
      }
    }

    syncRoyalFloatingLabel() {
      if (this.kind !== 'royal-watchware' || !this.floatingButton || !this.floatingLabel) return;
      const hasExplicitModel = Boolean(this.royalModelFromUrl());
      const label = hasExplicitModel ? 'Try another model' : 'Select your model';
      this.floatingLabel.textContent = label;
      this.floatingButton.setAttribute('aria-label', label);
    }

    centerSelected() {
      const selected = this.options.find((option) => option.classList.contains('is-selected'));
      if (!selected) return;
      const rail = selected.closest('[data-collection-context-options]');
      if (!rail) return;
      if (!rail.classList.contains('is-overflowing')) {
        rail.scrollLeft = 0;
        return;
      }
      const target = selected.offsetLeft - ((rail.clientWidth - selected.offsetWidth) / 2);
      rail.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }

    syncFromUrl() {
      const params = new URLSearchParams(window.location.search || '');
      if (this.kind.includes('apple')) {
        const rememberedApple = this.deviceMemory?.get('appleWatch');
        const explicitSize = params.get('size') || '';
        const pageSize = this.root.dataset.currentAppleSize || '';
        const isGenericAppleCases = this.root.dataset.collectionHandle === 'apple-watch-cases';
        // A remembered size is useful product context, but the canonical
        // /apple-watch-cases category means ALL Apple Watch cases. Do not turn
        // that route into a silent client-side 45mm/49mm collection. Exact
        // size routes and explicit ?size= links remain authoritative.
        const requestedSize = explicitSize || pageSize || (!isGenericAppleCases ? rememberedApple?.size || '' : '');
        if (requestedSize) {
          const match = this.options.find((option) => sizeNorm(option.dataset.contextSize) === sizeNorm(requestedSize));
          const size = match ? match.dataset.contextSize : requestedSize;
          const series = match ? match.dataset.contextSeries || '' : '';
          const image = match ? match.dataset.contextImage || '' : '';
          this.applyAppleSize(size, series, image, false);
        } else {
          // The compact bar must describe the collection actually on screen.
          // /apple-watch-cases is ALL sizes, therefore memory must never render a
          // remembered 41/45/49mm value in the bar. Memory may only pre-highlight
          // the matching choice inside the selector and improve compatible PDP links.
          this.selectedSize = '';
          this.updateEditionLinks('');
          this.filterProductsBySize('');
          this.renderAppleValue('', '', '');

          const rememberedMatch = isGenericAppleCases && rememberedApple?.size
            ? this.options.find((option) => sizeNorm(option.dataset.contextSize) === sizeNorm(rememberedApple.size))
            : null;

          if (rememberedMatch) {
            const rememberedSize = rememberedMatch.dataset.contextSize || rememberedApple.size;
            this.setSelectedOption('size', rememberedSize);
            this.updateWatchwareProductLinks({ size: rememberedSize, compatibleOnly: true });
          } else {
            this.setSelectedOption('size', '');
          }
        }
      }

      if (this.kind.includes('iphone')) {
        const rememberedIphone = this.deviceMemory?.get('iphone');
        const explicitDevice = params.get('iphone_device') || '';
        const rememberedMatchesPage = !this.currentIphoneGeneration
          || !rememberedIphone?.generation
          || norm(rememberedIphone.generation) === norm(this.currentIphoneGeneration);
        const requestedDevice = explicitDevice || (rememberedMatchesPage ? rememberedIphone?.handle || '' : '');
        const match = this.iphoneOptionForValue(requestedDevice);
        const requestedGeneration = match?.dataset.contextGeneration || '';
        const generationMatchesPage = !this.currentIphoneGeneration
          || !requestedGeneration
          || norm(requestedGeneration) === norm(this.currentIphoneGeneration);
        if (match && generationMatchesPage) {
          this.applyIphoneDevice(match, false);
        } else {
          this.selectedIphoneDevice = '';
          this.setSelectedOption('iphone', '');
          this.renderIphoneValue('', this.currentIphoneGeneration, '');
          this.filterProductsByIphone('');
        }
      }

      if (this.kind.includes('royal')) {
        const rememberedRoyal = this.deviceMemory?.get('royalPop');
        const rememberedRoyalSlug = rememberedRoyal?.modelSlug || rememberedRoyal?.handle || '';
        const explicitRoyal = params.get('model') || '';
        const requestedModel = explicitRoyal || rememberedRoyalSlug || this.root.dataset.defaultRoyalSlug || '';
        let match = this.royalOptionForValue(requestedModel);
        if (!match) match = this.royalOptionForValue(this.root.dataset.defaultRoyalSlug);
        if (match) {
          this.applyRoyalModel(match, false);
          if (!explicitRoyal && rememberedRoyalSlug && norm(match.dataset.contextModel) === norm(rememberedRoyalSlug)) {
            document.dispatchEvent(new CustomEvent('royal-pop:model-change', {
              detail: { modelSlug: rememberedRoyalSlug, source: 'collection-context-navigation' }
            }));
          }
        }
      }
      this.syncRoyalFloatingLabel();
      this.updateCount();
    }


    onProductsAdded() {
      if (this.kind === 'apple-watchware') {
        const isGenericAppleCases = this.root.dataset.collectionHandle === 'apple-watch-cases';
        this.filterProductsBySize(this.selectedSize || '');
        if (this.selectedSize) {
          this.updateWatchwareProductLinks({ size: this.selectedSize });
        } else if (isGenericAppleCases) {
          const rememberedApple = this.deviceMemory?.get('appleWatch');
          if (rememberedApple?.size) this.updateWatchwareProductLinks({ size: rememberedApple.size, compatibleOnly: true });
        }
      } else if (this.kind === 'royal-watchware' && this.selectedModel) {
        this.updateWatchwareProductLinks({ model: this.selectedModel });
      } else if (this.kind.includes('iphone') && this.selectedIphoneDevice) {
        this.filterProductsByIphone(this.selectedIphoneDevice);
        this.updateIphoneProductLinks(this.selectedIphoneDevice);
      } else {
        this.updateCount();
      }
    }

    onExternalRoyalChange(event) {
      if (!this.kind.includes('royal')) return;
      if (event?.detail?.source === 'collection-context-navigation') return;
      const requested = event?.detail?.modelSlug;
      if (requested == null || requested === '') return;
      const match = this.royalOptionForValue(requested);
      if (match) this.applyRoyalModel(match, false);
    }

    updateEditionLinks(size) {
      this.root.querySelectorAll('[data-collection-edition-link]').forEach((link) => {
        const url = new URL(link.href, window.location.origin);
        if (link.dataset.editionKey === 'all') {
          url.pathname = size ? `/collections/apple-watch-${size}` : '/collections/apple-watch-cases';
          url.searchParams.delete('size');
        } else if (size) {
          url.searchParams.set('size', size);
        } else {
          url.searchParams.delete('size');
        }
        link.href = `${url.pathname}${url.search}${url.hash}`;
      });
    }

    filterProductsBySize(size) {
      if (this.kind !== 'apple-watchware') return;
      document.querySelectorAll('[data-collection-product]').forEach((item) => {
        const availabilityKnown = item.dataset.sizeAvailabilityKnown === 'true';
        const available = item.dataset.availableSizes || '';
        const shouldShow = !size || !availabilityKnown || available.includes(`|${sizeNorm(size)}|`);
        item.hidden = !shouldShow;
      });
      this.updateCount();
    }

    renderCount(value) {
      if (!this.count) return;
      const count = Number.isFinite(value) ? value : 0;
      this.count.textContent = '';
      const number = document.createElement('span');
      number.textContent = String(count);
      const label = document.createElement('span');
      label.className = 'collection-context__count-label';
      label.textContent = count === 1 ? ' product' : ' products';
      this.count.append(number, label);
    }

    updateCount() {
      if (!this.count) return;
      if (this.kind.includes('iphone') && this.selectedIphoneDevice) {
        const visible = Array.from(document.querySelectorAll('[data-collection-product]')).filter((item) => !item.hidden).length;
        this.renderCount(visible);
        return;
      }
      const genericAppleWithQuery = this.root.dataset.collectionHandle === 'apple-watch-cases' && this.selectedSize;
      if (this.kind === 'apple-watchware' && this.selectedSize && (this.routeMode === 'query' || genericAppleWithQuery)) {
        const visible = Array.from(document.querySelectorAll('[data-collection-product]')).filter((item) => !item.hidden).length;
        this.renderCount(visible);
        return;
      }
      this.renderCount(this.originalCount);
    }

    updateWatchwareProductLinks(context) {
      if (this.kind !== 'apple-watchware' && this.kind !== 'royal-watchware') return;
      document.querySelectorAll('[data-collection-product]').forEach((item) => {
        const availabilityKnown = item.dataset.sizeAvailabilityKnown === 'true';
        const available = item.dataset.availableSizes || '';
        const sizeIsPurchasable = !context.size || !availabilityKnown || available.includes(`|${sizeNorm(context.size)}|`);
        item.querySelectorAll('a[href*="/products/"]').forEach((link) => {
          const url = new URL(link.href, window.location.origin);
          if (context.size && sizeIsPurchasable) url.searchParams.set('size', context.size);
          else if (context.size && context.compatibleOnly) url.searchParams.delete('size');
          if (context.model) url.searchParams.set('model', context.model);
          link.href = `${url.pathname}${url.search}${url.hash}`;
        });
      });
    }
  }

  const boot = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => new CollectionContextNavigation(root));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target || document));
})();
